<script setup lang="ts">
import {computed, ref, watch} from 'vue'
import {useDebounceFn} from '@vueuse/core';
import type {ProductListInterface} from "~~/server/types/Products";
import {usePrice} from "~/composables/usePrice";
import {tracking} from "~/services/tracking.client";
import { ClickOutside as vClickOutside } from 'element-plus'

const { format: price } = usePrice();
const { $api } = useNuxtApp();
const { t } = useI18n();


const query = ref<string|null>('')
const loading = ref(false)
const results = ref<ProductListInterface[]>([])
const activeCategoryUrl = ref<string | null>(null)

const filteredResults = computed(() => {
    const url = activeCategoryUrl.value
    if (!url) return results.value ?? []
    return (results.value ?? []).filter(p => (p.categories_list ?? []).some(c => c.url === url))
})
const selectCategory = (url: string | null) => activeCategoryUrl.value = url;


const normalize = (s: string|null|undefined) => (s ?? '').toString().trim().toLowerCase();

const searchRemote = async (q: string): Promise<ProductListInterface[]> => await $api<ProductListInterface[]>('products/search', {
    method: 'POST',
    body: {q}
});
const runSearch = useDebounceFn(async (qRaw: string|null) => {
    const q = normalize(qRaw)
    activeCategoryUrl.value = null

    if (q.length < 3) {
        results.value = [] as ProductListInterface[]
        loading.value = false
        return
    }
    tracking.search(q);


    loading.value = true
    try {
        const res = await searchRemote(q)
        results.value = res ?? ([] as ProductListInterface[])
        tracking.viewItemList(results.value, 'search_results')
    } catch (e) {
        results.value = []
    } finally {
        loading.value = false
    }
}, 250)
const categories = computed(() => {
    const map = new Map();
  map.set(0, {name: t('all') ,url: '/products',count: results.value?.length || 0,all:true})
    for (const p of results.value ?? []) {
        for (const c of p.categories_list ?? []) {
            const existing = map.get(c.id)
            if (existing) existing.count++;
            else map.set(c.id, {name: c.name,url: c.url,count: 1,})
        }
    }
    return [...map.values()]
})
watch(query, (q) => {
    loading.value = true
    runSearch(q)
}, { immediate: true })
const show=ref(false);
const selectedCategory=ref(null);

watch(
    ()=>selectedCategory.value,
    (cat)=>selectCategory(cat)
)

const isMobile=computed(()=>window.innerWidth < 992)
const hideSearch=()=>show.value=false;
</script>

<template>
    <div class="search-box" v-click-outside="hideSearch">
        <el-popover

                :trigger-keys="['enter']"
                placement="bottom"
                :visible="show"
                :teleported="false"
                popper-class="search-popover"
                :width="1100"
        >
            <template #reference>
                <el-input
                        v-model="query"
                        :aria-label="$t('search')"
                        :placeholder="$t('search')+'...'"
                        clearable
                        class="search-input"
                        @click="show=true"
                >
                    <template #suffix>
                        <el-button link :aria-label="$t('search')" :loading="loading" style="color:var(--footer-color);">
                          <Icon name="material-symbols-light:search" size="24" v-if="!loading" />
                        </el-button>
                    </template>
                </el-input>
            </template>

            <div v-loading="loading" class="results">
                <div v-if="query && query.length > 2" class="items">
                    <template v-if="results.length">
                            <div class="row g-0">

                                <!-- LEFT: categories -->
                                <aside class="col-12 col-lg-4  border-end-md">
                                    <div class="d-flex align-items-center justify-content-between px-2 py-2 facet-sticky">
                                      <span class="fw-bold small">Категории</span>
                                    </div>

                                    <div class="facet-scroll d-flex flex-wrap flex-md-column gap-1 is-round ">
                                      <el-segmented :options="categories"  :direction="isMobile ? 'horizontal':'vertical'" v-model="selectedCategory"  :props="{ value: 'url', label: 'name' }">
                                        <template #default="scope">
                                          <div class="d-flex align-items-center justify-content-between">
                                          <span class="me-2 text-truncate " style="max-width: 170px">{{ scope.item.name }}</span>

                                            <div v-if="!scope.item?.all" class="d-flex align-items-center gap-2">
                                          <el-tag size="small"  class="dark-color" effect="plain">{{ scope.item.count }}</el-tag>
                                            <NuxtLinkLocale :to="scope.item.url" @click="show=false" style="display:flex;gap: 4px;">{{$t('view_all')}}<Icon name="material-symbols-light:arrow-forward" size="16" /></NuxtLinkLocale>
                                            </div>
                                          </div>
                                        </template>
                                      </el-segmented>

                                    </div>
                                </aside>

                                <!-- RIGHT: results -->
                                <section class="col-12 col-lg-8 ">
                                    <div class="d-flex align-items-center justify-content-between px-2 py-2 list-sticky">
                                        <div class="dark-color" type="info">{{ $t('found') }}: <b>{{ results.length }}</b></div>

                                        <NuxtLinkLocale
                                                class="small fw-semibold text-decoration-none"
                                                :to="`/products?sq=${encodeURIComponent(query)}`"
                                                @click="show=false"
                                        >
                                           {{$t('view_all')}}
                                        </NuxtLinkLocale>
                                    </div>

                                    <div class="pt-3 list-scroll">

                                            <div v-for="p in filteredResults" :key="p.id" class="mb-2 row g-2 align-items-center justify-content-between">
                                                <div class="col-auto ">
                                                <NuxtLinkLocale :to="p.url" @click="show=false">
                                                    <img :src="p.icon"
                                                         :alt="p.name"
                                                         loading="lazy"
                                                         width="80"
                                                         height="80"
                                                         style="object-fit: contain; aspect-ratio: 4/3; height: auto" />
                                                </NuxtLinkLocale>
                                                </div>
                                                <div class="col">
                                                  <NuxtLinkLocale :to="p.url" @click="show=false">
                                                    <div class="dark-color">{{ p.name }}</div>
                                                  </NuxtLinkLocale>
                                                  <div class="d-flex mb-1 align-items-center justify-content-between mt-2">

                                                    <div class="fw-bold dark-color"> {{ price(p.price) }}</div>

                                                    <NuxtLinkLocale :to="p.categories_list?.[0]?.url" >
                                                      <el-tag size="small" effect="dark" round type="primary">{{ p.categories_list?.[0]?.name }}</el-tag>
                                                    </NuxtLinkLocale>
                                                  </div>
                                                </div>
                                            </div>
                                    </div>
                                </section>

                            </div>
                    </template>

                    <el-text type="warning" size="large" v-else>
                        {{ $t('no_search_results') }}
                    </el-text>
                </div>

                <div v-else>
                    {{ $t('fast_search_info') }}
                </div>
            </div>
        </el-popover>
    </div>

</template>

<style scoped lang="scss">
:deep(.el-input__clear) {
  color: #212121;
  cursor: pointer;
  font-size: 18px;
}

:deep(.search-popover) {

  @media (max-width: 768px) {
    width: 100% !important;
    left: 0 !important;
  }

  border: 1px solid var(--el-border-color-lighter) !important;
  border-radius: 18px !important;
  overflow: hidden;
  background: var(--el-bg-color) !important;
  box-shadow: 0 18px 45px rgba(0,0,0,.14), 0 2px 10px rgba(0,0,0,.06) !important;
}

/* input polish */
:deep(.search-input .el-input__wrapper) {
  width: 300px;
  border-radius: 14px;
  height: 32px;
  box-shadow: 0 0 0 2px var(--nav-bg) inset;

  &:hover, &:focus {
    box-shadow: 0 0 0 2px var(--el-color-primary)  inset;
  }
}

/* sticky headers */
.facet-sticky, .list-sticky {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--el-bg-color);
  border-bottom: 1px solid var(--el-border-color-lighter);
}

.el-segmented{
  padding: 0!important;
}


/* scroll areas */
.facet-scroll { max-height: 380px; overflow: auto; border-radius: 20px; margin-top: 10px; }
.list-scroll  { max-height: 380px; overflow-y: auto; overflow-x: hidden }

/* make Element text buttons feel like pills */
:deep(.facet-btn.el-button.is-text) {
  width: 100%;
  justify-content: space-between;
  border-radius: 999px;
  padding: 6px 12px;
}

@media (max-width: 768px) {
  :deep(.facet-btn.el-button.is-text) { width: auto; }
}



</style>
